import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    shouldLoadCart: !getCurrentCartState(state).value,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    clickableLink?: ClickablePresentationProps;
    cartIcon?: IconPresentationProps;
    totalBadge?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    clickableLink: {
        css: css`
            position: relative;
        `,
    },
    cartIcon: {
        css: css`
            background: ${getColor("primary")};
            border-radius: 50%;
            justify-content: center;

            svg circle {
                r: 2px;
            }
            transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
            &:hover {
                background-color: ${props => resolveColor("primary.contrast", props.theme)};
                border-color: ${props => resolveColor("primary.contrast", props.theme)};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 30px;
                            width: 30px;
                            > svg {
                                height: 11px;
                                width: 11px;
                            }
                        `,
                        null,
                        null,
                        css`
                            height: 52px;
                            width: 52px;
                            > svg {
                                height: 18px;
                                width: 21px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    totalBadge: {
        css: css`
            background: ${getColor("danger")};
            border-radius: 50%;
            width: 20px;
            height: 20px;
            text-align: center;
            color: ${getColor("danger.contrast")};
            box-sizing: border-box;
            border: 2px solid ${getColor("danger.contrast")};
            font-size: 8.5px;
            padding: 1.5px;
            position: absolute;
            top: -2px;
            left: -6px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

interface OwnProps {
    onBeforeNavigate?: (url: string | undefined) => void;
    extendedStyles?: CartLinkStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const onClick = (history: History, link: string | undefined) => {
    if (link) {
        history.push(link);
    }
};

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    loadCurrentCart,
    history,
    onBeforeNavigate,
    extendedStyles,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart();
        }
    }, [shouldLoadCart]);

    retainedTotalCountToDisplay = 0;
    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const handleOnClick = () => {
        if (onBeforeNavigate) {
            onBeforeNavigate(cartUrl);
        } else {
            onClick(history, cartUrl);
        }
    };

    const [styles] = React.useState(() => mergeToNew(cartLinkStyles, extendedStyles));

    return (
        <Clickable
            data-test-selector="cartLink"
            {...styles.clickableLink}
            onClick={handleOnClick}
            aria-label={translate("button, go to cart")}
        >
            <Icon src={ShoppingCart} {...styles.cartIcon} />
            {retainedTotalCountToDisplay > 0 && (
                <Typography {...styles.totalBadge} data-test-selector="cartLinkQuantity">
                    {retainedTotalCountToDisplay}
                </Typography>
            )}
        </Clickable>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(CartLink));
